import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Mountain, Tent, UtensilsCrossed, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";

const STATUSES = ["new", "confirmed", "completed", "cancelled"];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [tours, setTours] = useState([]);
  const [cafe, setCafe] = useState([]);
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState({ tours: 0, cafe: 0, new_tours: 0, new_cafe: 0, subscribers: 0 });

  const load = useCallback(async () => {
    try {
      const [t, c, n, s] = await Promise.all([
        api.get("/admin/tour-bookings"),
        api.get("/admin/cafe-reservations"),
        api.get("/admin/newsletter"),
        api.get("/admin/stats"),
      ]);
      setTours(t.data);
      setCafe(c.data);
      setSubs(n.data);
      setStats(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("aruha_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!localStorage.getItem("aruha_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const logout = () => {
    localStorage.removeItem("aruha_admin_token");
    navigate("/admin/login");
  };

  const updateStatus = async (kind, id, status) => {
    const path = kind === "tour" ? "tour-bookings" : "cafe-reservations";
    try {
      await api.patch(`/admin/${path}/${id}`, { status });
      toast.success("Status updated");
      if (kind === "tour") setTours((p) => p.map((b) => (b.id === id ? { ...b, status } : b)));
      else setCafe((p) => p.map((b) => (b.id === id ? { ...b, status } : b)));
    } catch {
      toast.error("Update failed");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0d110f]">
        <Loader2 className="h-6 w-6 animate-spin text-[#ba5a45]" />
      </div>
    );
  }

  const StatusSelect = ({ kind, row }) => (
    <Select value={row.status} onValueChange={(v) => updateStatus(kind, row.id, v)}>
      <SelectTrigger
        data-testid={`status-${row.id}`}
        className="h-8 w-[130px] rounded-none border-[#2c332f] bg-[#0d110f] text-xs text-[#f2efeb]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-none border-[#242a27] bg-[#151a18] text-[#f2efeb]">
        {STATUSES.map((s) => (
          <SelectItem key={s} value={s} className="text-xs capitalize focus:bg-[#ba5a45]/20 focus:text-[#f2efeb]">
            {s}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="min-h-screen bg-[#0d110f] text-[#f2efeb]">
      <header className="flex items-center justify-between border-b border-[#242a27] px-6 py-5 lg:px-10">
        <div className="flex items-center gap-3">
          <Mountain className="h-5 w-5 text-[#ba5a45]" />
          <div>
            <div className="font-serif text-xl">Aruha Trails</div>
            <div className="overline">Bookings Dashboard</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button
            data-testid="manage-content-btn"
            onClick={() => navigate("/admin/content")}
            variant="outline"
            className="rounded-none border-[#2c332f] bg-transparent text-xs uppercase tracking-wider text-[#d6d2cb] hover:bg-[#151a18] hover:text-[#f2efeb]"
          >
            Manage Content
          </Button>
          <Button
            data-testid="admin-logout-btn"
            onClick={logout}
            variant="outline"
            className="rounded-none border-[#2c332f] bg-transparent text-xs uppercase tracking-wider text-[#d6d2cb] hover:bg-[#151a18] hover:text-[#f2efeb]"
          >
            <LogOut className="mr-2 h-4 w-4" /> Logout
          </Button>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 py-10 lg:px-10">
        <div className="mb-10 grid grid-cols-2 gap-4 lg:grid-cols-4 lg:gap-6">
          <StatCard icon={Tent} label="Tour Enquiries" value={stats.tours} sub={`${stats.new_tours} new`} />
          <StatCard icon={UtensilsCrossed} label="Cafe Reservations" value={stats.cafe} sub={`${stats.new_cafe} new`} />
          <StatCard icon={Mail} label="Subscribers" value={stats.subscribers} sub="newsletter" />
          <StatCard icon={Tent} label="Needs Action" value={stats.new_tours + stats.new_cafe} sub="new" />
        </div>

        <Tabs defaultValue="tours">
          <TabsList className="rounded-none border border-[#242a27] bg-[#151a18] p-1">
            <TabsTrigger
              value="tours"
              data-testid="tab-tours"
              className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-[#ba5a45] data-[state=active]:text-[#f2efeb]"
            >
              Tour Enquiries ({tours.length})
            </TabsTrigger>
            <TabsTrigger
              value="cafe"
              data-testid="tab-cafe"
              className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-[#ba5a45] data-[state=active]:text-[#f2efeb]"
            >
              Cafe Reservations ({cafe.length})
            </TabsTrigger>
            <TabsTrigger
              value="newsletter"
              data-testid="tab-newsletter"
              className="rounded-none text-xs uppercase tracking-wider data-[state=active]:bg-[#ba5a45] data-[state=active]:text-[#f2efeb]"
            >
              Subscribers ({subs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="tours" className="mt-6">
            <DataTable
              testId="tours-table"
              empty="No tour enquiries yet."
              headers={["Guest", "Contact", "Experience", "Date", "Group", "Status"]}
              rows={tours}
              render={(b) => (
                <TableRow key={b.id} className="border-[#242a27] hover:bg-[#151a18]">
                  <TableCell className="font-medium text-[#f2efeb]">
                    {b.name}
                    <div className="text-xs text-[#7a6c58]">{new Date(b.created_at).toLocaleDateString()}</div>
                  </TableCell>
                  <TableCell className="text-[#a1a8a4]">
                    <div>{b.email}</div>
                    <div className="text-xs">{b.phone}</div>
                  </TableCell>
                  <TableCell className="text-[#d6d2cb]">{b.adventure}</TableCell>
                  <TableCell className="text-[#a1a8a4]">{b.preferred_date || "—"}</TableCell>
                  <TableCell className="text-[#a1a8a4]">{b.group_size}</TableCell>
                  <TableCell><StatusSelect kind="tour" row={b} /></TableCell>
                </TableRow>
              )}
            />
          </TabsContent>

          <TabsContent value="cafe" className="mt-6">
            <DataTable
              testId="cafe-table"
              empty="No cafe reservations yet."
              headers={["Guest", "Contact", "Meal", "Date & Time", "Party", "Status"]}
              rows={cafe}
              render={(b) => (
                <TableRow key={b.id} className="border-[#242a27] hover:bg-[#151a18]">
                  <TableCell className="font-medium text-[#f2efeb]">
                    {b.name}
                    <div className="text-xs text-[#7a6c58]">{new Date(b.created_at).toLocaleDateString()}</div>
                  </TableCell>
                  <TableCell className="text-[#a1a8a4]">
                    <div>{b.email}</div>
                    <div className="text-xs">{b.phone}</div>
                  </TableCell>
                  <TableCell className="text-[#d6d2cb]">{b.meal}</TableCell>
                  <TableCell className="text-[#a1a8a4]">{b.date} · {b.time}</TableCell>
                  <TableCell className="text-[#a1a8a4]">{b.party_size}</TableCell>
                  <TableCell><StatusSelect kind="cafe" row={b} /></TableCell>
                </TableRow>
              )}
            />
          </TabsContent>

          <TabsContent value="newsletter" className="mt-6">
            <DataTable
              testId="newsletter-table"
              empty="No subscribers yet."
              headers={["Email", "Name", "Subscribed"]}
              rows={subs}
              render={(b) => (
                <TableRow key={b.id} className="border-[#242a27] hover:bg-[#151a18]">
                  <TableCell className="font-medium text-[#f2efeb]">{b.email}</TableCell>
                  <TableCell className="text-[#a1a8a4]">{b.name || "—"}</TableCell>
                  <TableCell className="text-[#a1a8a4]">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                </TableRow>
              )}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-[#242a27] bg-[#151a18] p-6">
    <div className="flex items-center justify-between">
      <span className="overline">{label}</span>
      <Icon className="h-4 w-4 text-[#7a6c58]" />
    </div>
    <div className="mt-4 font-serif text-4xl text-[#f2efeb]">{value}</div>
    <div className="mt-1 text-xs text-[#7a6c58]">{sub}</div>
  </div>
);

const DataTable = ({ testId, headers, rows, render, empty }) => (
  <div data-testid={testId} className="border border-[#242a27] bg-[#0d110f]">
    {rows.length === 0 ? (
      <div className="p-12 text-center text-[#7a6c58]">{empty}</div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow className="border-[#242a27] hover:bg-transparent">
            {headers.map((h) => (
              <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-[#7a6c58]">
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>{rows.map(render)}</TableBody>
      </Table>
    )}
  </div>
);
